import { useState, useEffect, useRef } from "react";
import {
  Grid, TextField, Box, Button, Typography, Paper, InputAdornment, IconButton, MenuItem, Select, FormControl,
  FormControlLabel, Radio, RadioGroup, InputLabel,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  getCategorydrp,
  getsubCategorydrp,
  getwardendrp,
  getBed,
  getFloor,
  getserviceProvider,
  getservicedrp,
  getAmenitydrp,
  getProperty,
  insertRiseTicket,
} from "../MaintenanceApp/Api/RSTicketApi";
import { z } from "zod";
import axiosInstance from "../../lib/axios";
import CustomAutocomplete from "../AutoComplete/AutoComplete";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Dialog, IconButton as MuiIconButton, Tooltip } from '@mui/material';

const FORM_INIT_STATE = {
  PropId: "",
  userType: "warden",
  mobileNumber: "",
  raisedBy: "",
  ticketFor: "unit",
  ticketForText: "unit",
  categoryId: "",
  subCategoryId: "",
  serviceId: "",
  description: "",
  remark: "",
  attachments: [],
  roomBedName: "",
  floorId: "",
  amenityId: "",
  CommonAreaId: "",
  TktImage: "",   // Base64 string
  ImgName: "",    // Only filename
};

const raiseServiceTicketSchema = z
  .object({
    PropId: z.number().min(1, "Property is required"),
    categoryId: z.number().min(1, "Category is required"),
    subCategoryId: z.number().min(1, "Subcategory is required"),
    serviceId: z.number().min(1, "Service/complaint is required"),
    floorId: z.number().optional(),
    CommonAreaId: z.string().optional(),
    amenityId: z.string().optional(),
    roomBedName: z.string().optional(),
  })
  .refine((data) => {
    const { ticketFor, roomBedName, floorId, CommonAreaId, amenityId } = data;
    if (ticketFor === "unit" && !roomBedName) return false;
    if (ticketFor === "floor" && !floorId) return false;
    if (ticketFor === "commonArea" && !CommonAreaId) return false;
    if (ticketFor === "amenity" && !amenityId) return false;
    return true;
  });

const commonFieldSx = {
  "& .MuiFilledInput-root": {
    backgroundColor: "transparent",
    border: "1px solid #d0d0d0",
    borderRadius: 1,
    transition: "border-color 0.2s ease",
    "&:before, &:after": { display: "none" },
    "&:hover": { borderColor: "#a0a0a0" },
    "&.Mui-focused": { borderColor: "#635bff", boxShadow: "none" },
  },
  "& .MuiInputLabel-filled": {
    color: "inherit",
    fontWeight: "inherit",
    "&.Mui-focused": { color: "inherit" },
  },
};

const ResRaiseTicket = () => {
  const ResidentId = localStorage.getItem("ResidentId");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    ...FORM_INIT_STATE,
    ticketFor: "unit",
  });
  const [selectedImage, setSelectedImage] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [floorList, setFloorList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [amenityList, setAmenityList] = useState([]);
  const [isTicketForDisabled, setTicketForDisabled] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState("warden");
  const [isPropertyDisabled, setIsPropertyDisabled] = useState(false);
  const [isAmenityDisabled, setIsAmenityDisabled] = useState(false);
  const [commonAreaList, setCommonAreaList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const PropertyId = localStorage.getItem("PropertyId");
  const residentName = localStorage.getItem("ResidentName");
  const mobileNumber = localStorage.getItem("Mobile");
  const roomBedId = localStorage.getItem("RoomBedId");
  const roomBedName = localStorage.getItem("RoomBedName");
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomSrc, setZoomSrc] = useState('');

  // Fetch properties
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await getProperty({ Flag: "prop", UserId: PropertyId });
        if (response && response.data) {
          const { Status, Data } = response.data;
          if (Status === 0 && Array.isArray(Data)) {
            setPropertyList(Data);
            setForm((prev) => ({
              ...prev,
              PropId: Data[0]?.Id ? String(Data[0].Id) : "",
            }));
          }
        }
      } catch (error) {
        toast.error("Failed to fetch properties.");
      }
    };
    fetchProperties();
  }, [PropertyId]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (form.PropId) {
          const response = await getCategorydrp({
            PropId: Number(form.PropId),
            flag: "Cat",
          });
          const { data: { Status, Data } } = response;
          if (Status === 0 && Array.isArray(Data) && Data.length > 0) {
            setCategoryList(Data);
            setForm((prev) => ({
              ...prev,
              categoryId: Data[0]?.Id ? String(Data[0].Id) : "",
              subCategoryId: "",
              serviceId: "",
            }));
          } else {
            setCategoryList([]);
            setForm((prev) => ({
              ...prev,
              categoryId: "",
              subCategoryId: "",
              serviceId: "",
            }));
          }
        }
      } catch (error) {
        toast.error("Failed to fetch categories.");
      }
    };
    fetchCategories();
  }, [form.PropId]);

  // Fetch subcategories
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        if (form.categoryId) {
          setSubcategoryList([]);
          setServiceList([]);
          setForm((prev) => ({
            ...prev,
            subCategoryId: "",
            serviceId: "",
          }));

          const response = await getsubCategorydrp({
            flag: "",
            TktCategoryId: Number(form.categoryId),
          });
          const { data: { Status, Data } } = response;

          if (Status === 0 && Array.isArray(Data) && Data.length > 0) {
            // const uniqueSubcategories = Data.filter(
            //   (subcategory, index, self) =>
            //     index === self.findIndex((s) => s.Id === subcategory.Id)
            // );
            // setSubcategoryList(uniqueSubcategories);
            // setForm((prev) => ({
            //   ...prev,
            //   subCategoryId: uniqueSubcategories[0]?.Id ? String(uniqueSubcategories[0].Id) : "",
            //   serviceId: "",
            // }));
            setSubcategoryList(Data);
            // DO NOT auto-select first subcategory
            setForm((prev) => ({
              ...prev,
              subCategoryId: "",  // Keep empty until user selects
              serviceId: "",      // Also reset service when subcategory changes
            }));
          } else {
            setSubcategoryList([]);
            setForm((prev) => ({
              ...prev,
              subCategoryId: "",
              serviceId: "",
            }));
          }
        } else {
          setSubcategoryList([]);
          setServiceList([]);
          setForm((prev) => ({
            ...prev,
            subCategoryId: "",
            serviceId: "",
          }));
        }
      } catch (error) {
        setSubcategoryList([]);
        setForm((prev) => ({
          ...prev,
          subCategoryId: "",
          serviceId: "",
        }));
        toast.error("Failed to fetch subcategories.");
      }
    };
    fetchSubcategories();
  }, [form.categoryId]);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        if (form.subCategoryId) {
          setServiceList([]);
          setForm((prev) => ({
            ...prev,
            serviceId: "",
          }));

          const response = await getservicedrp({
            TktSubCategoryId: Number(form.subCategoryId),
            Flag: "Tk",
          });
          const { data: { Status, Data } } = response;

          if (Status === 0 && Array.isArray(Data) && Data.length > 0) {
            // const uniqueServices = Data.filter(
            //   (service, index, self) =>
            //     index === self.findIndex((s) => s.Id === service.Id)
            // );
            // setServiceList(uniqueServices);
            // setForm((prev) => ({
            //   ...prev,
            //   serviceId: uniqueServices[0]?.Id ? String(uniqueServices[0].Id) : "",
            // }));
            setServiceList(Data);
            setForm((prev) => ({
              ...prev,
              // serviceId: Data[0]?.Id || "",
              serviceId: Data || "",
            }));
          } else {
            setServiceList([]);
            setForm((prev) => ({
              ...prev,
              serviceId: "",
            }));
          }
        } else {
          setServiceList([]);
          setForm((prev) => ({
            ...prev,
            serviceId: "",
          }));
        }
      } catch (error) {
        setServiceList([]);
        setForm((prev) => ({
          ...prev,
          serviceId: "",
        }));
        toast.error("Failed to fetch services.");
      }
    };
    fetchServices();
  }, [form.subCategoryId]);

  // Fetch amenities
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        if (form.ticketFor === "amenity") {
          const response = await getAmenitydrp({
            PropId: Number(form.PropId),
            flag: "Am",
          });
          if (response && response.data) {
            const { Status, Data } = response.data;
            if (Status === 0 && Array.isArray(Data)) {
              setAmenityList(Data);
              setForm((prev) => ({
                ...prev,
                amenityId: Data[0]?.Id ? String(Data[0].Id) : "",
              }));
            } else {
              setAmenityList([]);
              setForm((prev) => ({
                ...prev,
                amenityId: "",
              }));
            }
          }
        }
      } catch (error) {
        toast.error("Failed to fetch amenities.");
      }
    };
    fetchAmenities();
  }, [form.ticketFor, form.PropId]);

  // Fetch beds
  useEffect(() => {
    const fetchBeds = async () => {
      try {
        if (form.ticketFor === "unit" && form.PropId) {
          const response = await getBed({ PropId: Number(form.PropId), flag: "Bd" });
          const { data: { Status, Data } } = response;
          if (Status === 0 && Array.isArray(Data)) {
            setUnitList(Data);
            setForm((prev) => ({
              ...prev,
              roomBedName: roomBedName || (Data[0]?.Name ? String(Data[0].Name) : ""),
            }));
          } else {
            setUnitList([]);
            setForm((prev) => ({
              ...prev,
              roomBedName: roomBedName || "",
            }));
          }
        }
      } catch (error) {
        toast.error("Failed to fetch beds.");
      }
    };
    fetchBeds();
  }, [form.ticketFor, form.PropId, roomBedName]);

  // Fetch floors
  useEffect(() => {
    const fetchFloors = async () => {
      try {
        if (form.PropId) {
          const floorResponse = await getFloor({
            PropId: Number(form.PropId),
            Flag: "Fl",
          });
          const { data: { Status: floorStatus, Data: floorData } } = floorResponse;
          if (floorStatus === 0 && Array.isArray(floorData)) {
            const uniqueFloors = floorData.filter(
              (floor, index, self) =>
                index === self.findIndex((f) => f.Id === floor.Id)
            );
            setFloorList(uniqueFloors);
            setForm((prev) => ({
              ...prev,
              floorId: uniqueFloors[0]?.Id ? String(uniqueFloors[0].Id) : "",
            }));
          } else {
            setFloorList([]);
          }
        }
      } catch (error) {
        toast.error("Failed to fetch floors.");
      }
    };
    fetchFloors();
  }, [form.PropId]);

  // Fetch common areas
  useEffect(() => {
    const fetchCommonAreas = async () => {
      try {
        const response = await axiosInstance.post(
          "MstCommonArea/GetMstCommonAreaDrp"
        );
        const { data: { Status, Data } } = response;
        if (Status === 0 && Array.isArray(Data)) {
          setCommonAreaList(Data);
          setForm((prev) => ({
            ...prev,
            CommonAreaId: Data[0]?.CommonAreaId
              ? String(Data[1]?.CommonAreaId) : "",
          }));
        } else {
          setCommonAreaList([]);
          setForm((prev) => ({
            ...prev,
            CommonAreaId: "",
          }));
        }
      } catch (error) {
        toast.error("Failed to fetch common areas.");
      }
    };
    fetchCommonAreas();
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "PropId" && {
        categoryId: "",
        subCategoryId: "",
        serviceId: "",
      }),
      ...(name === "categoryId" && {
        subCategoryId: "",
        serviceId: "",
      }),
      ...(name === "subCategoryId" && {
        serviceId: "",
      }),
    }));
  };

  const handleBodyChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTicketForChange = (e) => {
    const selectedTicket = e.target.value;
    setForm((prev) => ({
      ...prev,
      ticketFor: selectedTicket,
      ticketForText: selectedTicket,
      roomBedName: selectedTicket === "unit" ? prev.roomBedName : "",
      floorId: selectedTicket === "floor" ? prev.floorId : "",
      amenityId: selectedTicket === "amenity" ? prev.amenityId : "",
      CommonAreaId: selectedTicket === "commonArea" ? prev.CommonAreaId : "",
    }));
  };

  const fetchServiceProvider = async () => {
    try {
      const response = await axiosInstance.post(
        "/TrnTicketing/getPropWiseCatWrSprodrp",
        {
          PropId: Number(form.PropId),
          TktCategoryId: Number(form.categoryId),
          flag: "SP",
        }
      );
      const { Status, Data } = response.data;
      return Status === 0 && Array.isArray(Data) && Data.length > 0 ? Data[0].Id : 1;
    } catch (error) {
      return 1;
    }
  };

  const fetchWardenData = async () => {
    try {
      const response = await axiosInstance.post(
        "/TrnTicketing/getPropWiseCatWrSprodrp",
        {
          PropId: Number(form.PropId),
          TktCategoryId: Number(form.categoryId),
          flag: "Wr",
        }
      );
      const { Status, Data } = response.data;
      return Status === 0 && Array.isArray(Data) && Data.length > 0 ? Data[0].Id : 1;
    } catch (error) {
      return 1;
    }
  };

  // UPDATED: Handle image → Base64 + filename only
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, GIF)");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      const fileName = file.name;

      setForm((prev) => ({
        ...prev,
        TktImage: base64String,
        ImgName: fileName,
      }));
      setSelectedImage(base64String); // For preview
    };
    reader.onerror = () => {
      toast.error("Failed to read file.");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent double submission
    if (isSubmitting) return;

    const result = raiseServiceTicketSchema.safeParse({
      ...form,
      PropId: Number(form.PropId),
      categoryId: Number(form.categoryId),
      subCategoryId: Number(form.subCategoryId),
      serviceId: Number(form.serviceId),
      floorId: Number(form.floorId) || undefined,
      CommonAreaId: form.CommonAreaId ? String(form.CommonAreaId) : "",
      amenityId: form.amenityId ? String(form.amenityId) : "",
    });
    if (!result.success) {
      toast.info("Please fill in all required inputs correctly", { autoClose: 1000 });
      return;
    }

    if (!form.PropId || !form.categoryId) {
      toast.info("Property and Category are required", { autoClose: 1000 });
      return;
    }

    const ServiceProviderId = (await fetchServiceProvider()) || 1;
    const WardenId = (await fetchWardenData()) || 1;

    const payload = {
      UserId: 1,
      PropId: Number(form.PropId),
      ResidentId: ResidentId,
      MobileNo: form.mobileNumber,
      Name: form.raisedBy,
      TktFor: form.ticketFor === "unit" ? "U" : form.ticketFor === "floor" ? "F" : form.ticketFor === "commonArea" ? "C" : "A",
      BedId: form.ticketFor === "unit" ? Number(roomBedId) : 1,
      FloorId: form.ticketFor === "floor" ? Number(form.floorId) : 1,
      AmenityId: form.ticketFor === "amenity" ? Number(form.amenityId) : 1,
      CommonAreaId: form.ticketFor === "commonArea" ? Number(form.CommonAreaId) : 1,
      TktCategoryId: Number(form.categoryId),
      TktSubCategoryId: Number(form.subCategoryId),
      TicketTypeId: 1,
      TicketTagId: 1,
      TicketSeverityId: 1,
      WardenId: WardenId,
      TktServiceId: Number(form.serviceId),
      ServiceProvId: ServiceProviderId,
      EscalateId: 1,
      ForwardedId: 1,
      TktImage: form.TktImage,  // Base64
      ImgName: form.ImgName,    // Only filename
      TicketSt: "O",
      ReqFlg: "R",
      Reason: "Man",
      AcceptFlg: "N",
      AssignFlg: "N",
      Description: form.description,
      Remark: form.remark,
      status: "1",
      CreatedBy: 1,
    };

    // Disable button and show loading
    setIsSubmitting(true);

    try {
      const response = await insertRiseTicket(payload);
      const { data: { Status, Message } } = response;
      if (Status === 0 && Message) {
        toast.success(Message, { autoClose: 1000 });
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      } else {
        toast.error(Message || "Failed to submit ticket.", { autoClose: 1000 });
      }
    } catch (error) {
      toast.error("An error occurred while submitting the ticket.", { autoClose: 1000 });
    }
  };

  const handleBack = () => {
    navigate("/resident-profile");
  };

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      mobileNumber: mobileNumber || "",
      raisedBy: residentName || "",
      roomBedName: roomBedName || "",
    }));
  }, [mobileNumber, residentName, roomBedName]);

  return (
    <>
      <ToastContainer />
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{
          minHeight: "100vh",
          padding: "0",
          marginRight: "0",
        }}
      >
        <Grid item xs={12} md={10} lg={10}>
          <Paper
            elevation={3}
            style={{
              paddingInline: "16px",
              paddingBlock: "6px",
              borderRadius: "10px",
              marginBlock: "40px 30px",
              marginInline: "10px",
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton justifyContent={'flex-start'} onClick={() => navigate('/resident-profile')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h6"
                align="center"
                style={{ margin: "0", padding: "5px 0" }}
                gutterBottom
              >
                Raise Service Ticket
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <Grid container mb={2} spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="filled" className="custom-textfield">
                    <InputLabel>Property<span style={{ color: "red" }}>*</span></InputLabel>
                    <Select
                      value={form.PropId}
                      onChange={handleChange}
                      name="PropId"
                      label="Property"
                      disabled
                    >
                      {propertyList.length > 0 ? (
                        propertyList.map((property) => (
                          <MenuItem key={property.Id} value={String(property.Id)}>
                            {property.Name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No Property available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Mobile Number and Raised By */}
              <Grid container mb={2} spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    className="custom-textfield"
                    variant="filled"
                    label={<span>Mobile Number<span style={{ color: "red" }}>*</span></span>}
                    name="mobileNumber"
                    value={form.mobileNumber}
                    onChange={handleChange}
                    disabled={form.userType === "warden"}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    className="custom-textfield"
                    variant="filled"
                    label={<span>Raised By<span style={{ color: "red" }}>*</span></span>}
                    name="raisedBy"
                    value={form.raisedBy}
                    disabled={form.userType !== "auto"}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              {/* Ticket For */}
              <Grid container mb={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">Ticket For</Typography>
                  <RadioGroup
                    row
                    name="ticketFor"
                    value={form.ticketFor}
                    onChange={handleTicketForChange}
                  >
                    <FormControlLabel value="unit" control={<Radio />} label="Unit" />
                    <FormControlLabel value="floor" control={<Radio disabled={isPropertyDisabled} />} label="Floor" />
                    <FormControlLabel value="commonArea" control={<Radio />} label="Common Area" />
                    <FormControlLabel value="amenity" control={<Radio disabled={isAmenityDisabled} />} label="Amenity" />
                  </RadioGroup>
                </Grid>
              </Grid>

              {/* Unit, Floor, Amenity, Common Area Dropdowns */}
              <Grid container spacing={2} mb={2}>
                {form.ticketFor === "unit" && (
                  <Grid item xs={12} sm={6} md={6}>
                    <TextField
                      fullWidth
                      variant="filled"
                      className="custom-textfield"
                      label={<span>Bed No<span style={{ color: "red" }}>*</span></span>}
                      name="roomBedName"
                      value={form.roomBedName}
                      disabled
                    />
                  </Grid>
                )}
                {form.ticketFor === "floor" && (
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomAutocomplete
                      id="floor-select"
                      name="floorId"
                      label={<span>Floors<span style={{ color: "red" }}>*</span></span>}
                      options={floorList}
                      value={floorList.find((floor) => floor.Id == form.floorId) || null}
                      onChange={handleChange}
                      disabled={isPropertyDisabled}
                      fullWidth
                    />
                  </Grid>
                )}
                {form.ticketFor === "commonArea" && (
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomAutocomplete
                      id="CommonAreaId-autocomplete"
                      name="CommonAreaId"
                      label={<span>Common Area<span style={{ color: "red" }}>*</span></span>}
                      options={commonAreaList}
                      value={commonAreaList.find((a) => a.CommonAreaId == form.CommonAreaId) || null}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                )}
                {form.ticketFor === "amenity" && (
                  <Grid item xs={12} sm={6} md={6}>
                    <CustomAutocomplete
                      id="amenityId-autocomplete"
                      name="amenityId"
                      label={<span>Amenity<span style={{ color: "red" }}>*</span></span>}
                      options={amenityList}
                      value={amenityList.find((amenity) => amenity.Id == form.amenityId) || null}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    variant="filled"
                    className="custom-textfield"
                    label="Remark"
                    name="remark"
                    value={form.remark || ""}
                    onChange={(e) => handleBodyChange("remark", e.target.value)}
                  />
                </Grid>
              </Grid>

              {/* Category, Subcategory, Service Dropdowns */}
              <Grid item xs={12} className="form_field" sx={{ mb: 2 }}>
                <CustomAutocomplete
                  id="category-select"
                  name="categoryId"
                  label={<span>Category<span style={{ color: "red" }}>*</span></span>}
                  options={categoryList}
                  value={categoryList.find((category) => category.Id == form.categoryId) || null}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} className="form_field" sx={{ mb: 2 }}>
                <CustomAutocomplete
                  key={form.categoryId}
                  id="subcategory-select"
                  name="subCategoryId"
                  label={<span>Subcategory<span style={{ color: "red" }}>*</span></span>}
                  options={subcategoryList}
                  value={subcategoryList.find((subcategory) => subcategory.Id == form.subCategoryId) || null}
                  onChange={handleChange}
                  fullWidth
                  disabled={!form.categoryId || subcategoryList.length === 0}
                />
              </Grid>
              <Grid item xs={12} className="form_field" sx={{ mb: 2 }}>
                <CustomAutocomplete
                  key={form.subCategoryId}
                  id="service-select"
                  name="serviceId"
                  label={<span>Service/complaint<span style={{ color: "red" }}>*</span></span>}
                  options={serviceList}
                  value={serviceList.find((service) => service.Id == form.serviceId) || null}
                  onChange={handleChange}
                  fullWidth
                  disabled={!form.subCategoryId || serviceList.length === 0}
                />
              </Grid>

              {/* Upload Image + Description - Side by Side */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={1.5}
                  sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 2,
                      border: form.TktImage ? '2px solid #635bff' : '2px dashed #635bff',
                      bgcolor: '#fafafa',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all .2s',
                      '&:hover': { borderColor: '#ccc' },
                    }}
                  >
                    {/* Image or Placeholder */}
                    {form.TktImage ? (
                      <img
                        src={form.TktImage}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onClick={() => {
                          setZoomSrc(form.TktImage);
                          setZoomOpen(true);
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          textAlign: 'center',
                          px: 1,
                          fontSize: '0.8rem',
                          lineHeight: 1.3,
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%,-50%)',
                        }}
                      >
                        Click to Upload Image
                      </Typography>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />

                    {/* Delete Icon */}
                    {form.TktImage && (
                      <Tooltip title="Remove Image" arrow>
                        <MuiIconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm((p) => ({ ...p, TktImage: '', ImgName: '' }));
                            setSelectedImage('');
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'error.light', color: '#fff' },
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </MuiIconButton>
                      </Tooltip>
                    )}

                    {/* Zoom Icon */}
                    {form.TktImage && (
                      <Tooltip title="Zoom Image" arrow>
                        <MuiIconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setZoomSrc(form.TktImage);
                            setZoomOpen(true);
                          }}
                          sx={{
                            position: 'absolute',
                            bottom: 1,
                            right: -1,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'primary.main' },
                          }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </MuiIconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={10} md={10.5}>
                  <Box>
                    <TextField
                      fullWidth
                      className="custom-textfield"
                      variant="filled"
                      name="description"
                      label="Description"
                      value={form.description || ""}
                      onChange={(e) => handleBodyChange("description", e.target.value)}
                      multiline
                      rows={2}
                      sx={commonFieldSx}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Submit and Back Buttons */}
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                  sx={{
                    borderColor: "#635bff",
                    color: "#635bff",
                    "&:hover": { borderColor: "#dddbff", color: "#635bff" },
                    padding: "8px 16px",
                    marginRight: "10px",
                    fontSize: "14px",
                    borderRadius: "20px",
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: isSubmitting ? "#ccc" : "#635bff",
                    "&:hover": {
                      backgroundColor: isSubmitting ? "#ccc" : "#dddbff",
                    },
                    padding: "8px 16px",
                    fontSize: "14px",
                    borderRadius: "20px",
                  }}
                >
                  {/* Submit */}
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Box>
            </form>
            {/* Zoom Dialog */}
            <Dialog
              open={zoomOpen}
              onClose={() => setZoomOpen(false)}
              maxWidth="md"
              fullWidth
              BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)' } }}
              PaperProps={{
                sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'hidden', m: 2 },
              }}
            >
              <MuiIconButton
                onClick={() => setZoomOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: 'black',
                  '&:hover': { bgcolor: '#fff' },
                  zIndex: 10,
                  boxShadow: 3,
                }}
              >
                <CloseIcon />
              </MuiIconButton>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '90vh',
                  cursor: 'zoom-out',
                }}
                onClick={() => setZoomOpen(false)}
              >
                <Box
                  component="img"
                  src={zoomSrc}
                  alt="Zoomed"
                  sx={{
                    maxWidth: '90%',
                    maxHeight: '90%',
                    objectFit: 'contain',
                    borderRadius: 2,
                    boxShadow: 10,
                    userSelect: 'none',
                  }}
                />
              </Box>
            </Dialog>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default ResRaiseTicket;